import React from 'react';
import {
  Box, Typography, Stack, CircularProgress, Alert, Button,
  Pagination, FormControl, InputLabel, Select, MenuItem,
  Grid, Card, CardContent, Fade, Grow
} from '@mui/material';
import { Refresh, TrendingUp, EmojiEvents } from '@mui/icons-material';
import LeaderboardCard from './LeaderboardCard';

const LeaderboardTabPanel = ({
  data = [],
  loading = false,
  error = null,
  tabIndex,
  currentTab,
  period,
  isAuthenticated = false,
  onRefresh,
  // Pagination props
  page = 1,
  totalPages = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  // Sorting props
  sortBy = 'points,desc',
  onSortChange,
  // Animation props
  animateCards = true
}) => {
  // Tab configuration for styling
  const tabConfig = {
    weekly: {
      color: '#66bb6a',
      icon: '🗓️',
      title: 'Weekly Champions',
      subtitle: 'This week\'s environmental heroes',
      emptyTitle: 'No weekly champions yet!',
      emptySubtitle: 'Be the first to make a difference this week!'
    },
    monthly: {
      color: '#43a047',
      icon: '📅',
      title: 'Monthly Champions',
      subtitle: 'This month\'s eco leaders',
      emptyTitle: 'No monthly champions yet!',
      emptySubtitle: 'Start your eco journey and become this month\'s champion!'
    },
    alltime: {
      color: '#2e7d32',
      icon: '👑',
      title: 'Hall of Fame',
      subtitle: 'Greatest eco warriors of all time',
      emptyTitle: 'The Hall of Fame awaits!',
      emptySubtitle: 'Begin your legacy and join the greatest environmental champions!'
    }
  };

  const config = tabConfig[period] || tabConfig.weekly;

  // Don't render if not the active tab
  if (tabIndex !== currentTab) {
    return null;
  }

  // Loading State
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8
      }}>
        <CircularProgress
          size={80}
          thickness={4}
          sx={{
            color: config.color,
            mb: 3,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }}
        />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Loading {config.title}...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gathering the latest eco champions data
        </Typography>
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          borderRadius: 4,
          mb: 3,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          onRefresh && (
            <Button
              color="inherit"
              size="small"
              onClick={onRefresh}
              startIcon={<Refresh />}
            >
              Retry
            </Button>
          )
        }
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Oops! Something went wrong
        </Typography>
        <Typography variant="body2">
          {error}
        </Typography>
      </Alert>
    );
  }

  // Empty State
  if (!data || data.length === 0) {
    return (
      <Card
        elevation={8}
        sx={{
          textAlign: 'center',
          py: 8,
          background: `linear-gradient(135deg, ${config.color}10 0%, ${config.color}05 100%)`,
          border: `2px solid ${config.color}20`,
          borderRadius: 6
        }}
      >
        <CardContent>
          <Typography variant="h1" sx={{ fontSize: '5rem', mb: 2 }}>
            {config.icon}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2, color: config.color }}>
            {config.emptyTitle}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            {config.emptySubtitle}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<EmojiEvents />}
            sx={{
              background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`,
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 8,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${config.color}40`
              }
            }}
            href="/classify"
          >
            Start Your Eco Journey
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Fade in={true} timeout={600}>
      <Box>
        {/* Controls Section */}
        {(isAuthenticated || data.length > 0) && (
          <Box sx={{
            mb: 4,
            p: 3,
            background: 'rgba(255,255,255,0.7)',
            borderRadius: 4,
            backdropFilter: 'blur(10px)'
          }}>
            <Grid container spacing={2} alignItems="center">
              {isAuthenticated && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sort By</InputLabel>
                      <Select
                        value={sortBy}
                        onChange={(e) => onSortChange?.(e.target.value)}
                        label="Sort By"
                      >
                        {/* Fixed: Use array instead of Fragment for MUI Select */}
                        {period === 'alltime' ? [
                          <MenuItem key="rewardsPoints-desc" value="rewardsPoints,desc">🏆 Highest Points</MenuItem>,
                          <MenuItem key="rewardsPoints-asc" value="rewardsPoints,asc">📈 Lowest Points</MenuItem>,
                          <MenuItem key="displayName-asc" value="displayName,asc">🔤 Name A-Z</MenuItem>,
                          <MenuItem key="displayName-desc" value="displayName,desc">🔤 Name Z-A</MenuItem>,
                          <MenuItem key="createdAt-desc" value="createdAt,desc">📅 Newest First</MenuItem>,
                          <MenuItem key="createdAt-asc" value="createdAt,asc">📅 Oldest First</MenuItem>
                        ] : [
                          <MenuItem key="totalPoints-desc" value="totalPoints,desc">🏆 Highest Points</MenuItem>,
                          <MenuItem key="totalPoints-asc" value="totalPoints,asc">📈 Lowest Points</MenuItem>,
                          <MenuItem key="displayName-asc" value="displayName,asc">🔤 Name A-Z</MenuItem>,
                          <MenuItem key="displayName-desc" value="displayName,desc">🔤 Name Z-A</MenuItem>
                        ]}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Items per Page</InputLabel>
                      <Select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange?.(e.target.value)}
                        label="Items per Page"
                      >
                        <MenuItem value={5}>5 champions</MenuItem>
                        <MenuItem value={10}>10 champions</MenuItem>
                        <MenuItem value={20}>20 champions</MenuItem>
                        <MenuItem value={50}>50 champions</MenuItem>
                        {/* Authenticated users can see more */}
                        <MenuItem value={100}>100 champions</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={isAuthenticated ? 4 : 8}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    🌍 Showing {data.length} champions
                    {!isAuthenticated && data.length >= 50 && (
                      <Typography component="span" variant="caption" sx={{ display: 'block', color: 'orange' }}>
                        ⚠️ Limited view. Login to see all champions!
                      </Typography>
                    )}
                  </Typography>
                  {onRefresh && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={onRefresh}
                      startIcon={<Refresh />}
                      sx={{
                        borderColor: config.color,
                        color: config.color,
                        '&:hover': {
                          backgroundColor: `${config.color}10`,
                          borderColor: config.color
                        }
                      }}
                    >
                      Refresh
                    </Button>
                  )}
                </Stack>
              </Grid>

              {isAuthenticated && (
                <Grid item xs={12} md={2}>
                  <Typography variant="caption" color="text.secondary" textAlign="right">
                    Page {page} of {totalPages}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Leaderboard Cards */}
        <Stack spacing={3} sx={{ mb: 4 }}>
          {data.map((entry, index) => {
            const actualRank = entry.rank || ((page - 1) * pageSize + index + 1);

            return (
              <Grow
                key={`${entry.userId || entry.displayName || index}-${period}-${page}`}
                in={true}
                timeout={animateCards ? 300 + (index * 100) : 0}
                style={{
                  transformOrigin: '0 0 0'
                }}
              >
                <div>
                  <LeaderboardCard
                    entry={entry}
                    rank={actualRank}
                    showDetailedStats={isAuthenticated}
                    isCurrentUser={entry.isCurrentUser || false}
                    animated={animateCards}
                  />
                </div>
              </Grow>
            );
          })}
        </Stack>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => onPageChange?.(value)}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 700,
                  border: '2px solid transparent',
                  '&:hover': {
                    border: `2px solid ${config.color}50`
                  }
                },
                '& .Mui-selected': {
                  background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%) !important`,
                  color: 'white !important',
                  border: `2px solid ${config.color} !important`
                }
              }}
            />
          </Box>
        )}

      </Box>
    </Fade>
  );
};

export default LeaderboardTabPanel;
